import { collection, getDocs, doc, updateDoc, addDoc, Timestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../services/firebase';
import { hashEmail, normalizeEmail } from './crypto';
import { debug } from './debug';

/**
 * Migrates existing subscribers to the new two-collection structure
 * This should be run by an admin after deploying the new security rules
 */
export const migrateSubscribersToTokenStructure = async (): Promise<{
  success: boolean;
  migrated: number;
  errors: number;
  message: string;
}> => {
  try {
    debug.log('Starting subscriber migration');
    
    // Get all subscribers
    const subscribersRef = collection(db, 'subscribers');
    const querySnapshot = await getDocs(subscribersRef);
    
    if (querySnapshot.empty) {
      return {
        success: true,
        migrated: 0,
        errors: 0,
        message: 'No subscribers found to migrate'
      };
    }
    
    let migrated = 0;
    let errors = 0;
    
    // Process each subscriber
    for (const subscriberDoc of querySnapshot.docs) {
      try {
        const subscriberData = subscriberDoc.data();
        
        // Skip if already migrated
        if (subscriberData.tokenId) {
          debug.log('Subscriber already has tokenId, skipping:', subscriberDoc.id);
          continue;
        }
        
        // Generate a token ID
        const tokenId = uuidv4();
        
        // Hash the email
        const email = subscriberData.email;
        const emailHash = await hashEmail(email);
        
        // Create the public token record
        const tokenRecord = {
          tokenId,
          emailHash,
          language: subscriberData.language || 'en',
          status: subscriberData.status === 'active' ? 'active' : 'unsub',
          createdAt: subscriberData.subscriptionDate || Timestamp.now(),
          metadata: {
            // Include only non-PII metadata
            source: subscriberData.metadata?.source || '',
            referrer: subscriberData.metadata?.referrer || '',
            platform: subscriberData.metadata?.platform || '',
            screenWidth: subscriberData.metadata?.screenWidth || '',
            screenHeight: subscriberData.metadata?.screenHeight || '',
            timezone: subscriberData.metadata?.timezone || '',
            country: subscriberData.metadata?.country || '',
            region: subscriberData.metadata?.region || '',
            utm_source: subscriberData.metadata?.utm_source || '',
            utm_medium: subscriberData.metadata?.utm_medium || '',
            utm_campaign: subscriberData.metadata?.utm_campaign || '',
            createdAt: subscriberData.metadata?.createdAt || new Date().toISOString()
          }
        };
        
        // Add the token record
        const tokensRef = collection(db, 'subscriber_tokens');
        await addDoc(tokensRef, tokenRecord);
        
        // Update the subscriber with the tokenId
        await updateDoc(doc(db, 'subscribers', subscriberDoc.id), {
          tokenId,
          email: normalizeEmail(email) // Ensure email is normalized
        });
        
        debug.log(`Migrated subscriber: ${subscriberDoc.id} with tokenId: ${tokenId}`);
        migrated++;
      } catch (error) {
        debug.error(`Error migrating subscriber: ${subscriberDoc.id}`, error);
        errors++;
      }
    }
    
    const message = `Migration completed. Migrated ${migrated} subscribers with ${errors} errors.`;
    debug.log(message);
    
    return {
      success: true,
      migrated,
      errors,
      message
    };
  } catch (error) {
    debug.error('Migration failed:', error);
    
    return {
      success: false,
      migrated: 0,
      errors: 1,
      message: `Migration failed: ${error instanceof Error ? error.message : String(error)}`
    };
  }
};